import { useState } from 'react';
import { cn } from '@dru/ui';
import {
  FDI_QUADRANTS, TOOTH_CONDITIONS, formatToothChart, parseToothChart, serialiseToothChart,
  type ToothChart as Chart, type ToothConditionCode,
} from '@dru/clinical-math/dental';

/**
 * The tooth chart, drawn as a dentist reads it: upper arch over lower arch,
 * the patient's right on the reader's left, FDI numbers on every tooth.
 * Pick a condition, tap the teeth; tap a marked tooth to clear it. The
 * value is the JSON the DEN-CHART test stores.
 */
export function ToothChart({ value, onChange, disabled }: {
  value: string;
  onChange: (serialised: string) => void;
  disabled?: boolean;
}) {
  const chart = parseToothChart(value || '{}');
  const [condition, setCondition] = useState<ToothConditionCode>('CARIOUS');

  const set = (tooth: number) => {
    if (disabled) return;
    const next: Chart = { ...chart };
    const key = String(tooth);
    if (next[key] === condition) delete next[key]; else next[key] = condition;
    onChange(serialiseToothChart(next));
  };

  const tone = (code: ToothConditionCode | undefined) => {
    const c = TOOTH_CONDITIONS.find((x) => x.code === code);
    switch (c?.tone) {
      case 'danger': return 'border-danger-600 bg-danger-100 text-danger-600';
      case 'warning': return 'border-warning-600 bg-warning-100 text-warning-600';
      case 'info': return 'border-primary-600 bg-primary-50 text-primary-700';
      case 'neutral': return 'border-neutral-500 bg-neutral-200 text-neutral-700 line-through';
      default: return 'border-neutral-300 bg-white text-neutral-800 hover:bg-neutral-50';
    }
  };

  const arch = (keys: string[], label: string) => (
    <div className="space-y-1">
      <div className="text-micro uppercase tracking-wide text-neutral-500">{label}</div>
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
        {keys.map((k) => {
          const q = FDI_QUADRANTS.find((x) => x.key === k)!;
          return (
            <div key={k} className="flex items-center gap-0.5" role="group" aria-label={q.label}>
              {q.teeth.map((t) => {
                const c = chart[String(t)];
                const short = TOOTH_CONDITIONS.find((x) => x.code === c)?.short;
                return (
                  <button
                    key={t}
                    type="button"
                    disabled={disabled}
                    onClick={() => set(t)}
                    aria-pressed={!!c}
                    aria-label={`Tooth ${t}${c ? `, ${TOOTH_CONDITIONS.find((x) => x.code === c)?.label}` : ''}`}
                    title={c ? TOOTH_CONDITIONS.find((x) => x.code === c)?.label : `Tooth ${t}`}
                    className={cn('flex h-9 w-7 flex-col items-center justify-center rounded border text-micro tabular-nums max-md:h-10 max-md:w-8', tone(c))}
                  >
                    <span className="font-medium">{t}</span>
                    {short && <span className="text-[9px] leading-none">{short}</span>}
                  </button>
                );
              })}
            </div>
          );
        })}
      </div>
    </div>
  );

  return (
    <div className="w-full space-y-2" data-testid="tooth-chart">
      {!disabled && (
        <div className="flex flex-wrap items-center gap-1" role="radiogroup" aria-label="Condition to mark">
          {TOOTH_CONDITIONS.map((c) => (
            <button
              key={c.code}
              type="button"
              role="radio"
              aria-checked={condition === c.code}
              onClick={() => setCondition(c.code)}
              className={cn('h-8 rounded-btn border px-2 text-caption', condition === c.code ? 'border-primary-600 bg-primary-50 font-semibold text-primary-700' : 'border-neutral-300 bg-white text-neutral-700')}
            >
              {c.label}
            </button>
          ))}
        </div>
      )}
      {arch(['UR', 'UL'], 'Upper jaw (maxilla) — patient’s right to left')}
      {arch(['LR', 'LL'], 'Lower jaw (mandible)')}
      <div className="flex flex-wrap items-center justify-between gap-2 text-caption text-neutral-600">
        <span>{formatToothChart(value || '{}')}</span>
        {!disabled && Object.keys(chart).length > 0 && (
          <button type="button" className="underline" onClick={() => onChange('{}')}>Clear all</button>
        )}
      </div>
    </div>
  );
}
